import { DEFAULT_PRINT_PRESETS, resolvePrintLayout } from "@fiket/booth-schema";
import type { OutputPlan, PrintPreset, SizeMm } from "@fiket/booth-schema";

/**
 * 인쇄 시트가 쓸 프리셋 — 활성 인쇄 프리셋(config.outputPlan.preset). config 를 아직 못 받았거나
 * 구버전 API 면 기본 세로(114.3×152.4 mm) 로 폴백해 최소한 여백 없는 세로 인쇄는 되게 한다.
 */
export function resolvePrintPreset(plan: OutputPlan | null | undefined): PrintPreset {
  return plan?.preset ?? DEFAULT_PRINT_PRESETS[0];
}

/** 114.3 → "114.3mm", 148 → "148mm", 3.15 → "3.15mm" (레이아웃 오프셋은 소수 2자리까지) */
function mm(n: number): string {
  return `${n}mm`;
}

/**
 * 활성 프리셋 규격으로 인쇄 페이지를 선언하는 CSS (BOOTH-57 + 용지 규격).
 *
 * 고객 인쇄물에 상하 큰 여백 + 브라우저 헤더/푸터(URL·날짜·페이지) 가 찍히던 원인은 `@page` 가 없어
 * 브라우저 기본 용지·여백을 썼고 사진을 `object-fit: contain` 으로 놓았기 때문. 여기서는
 * - `@page size` 를 **용지** mm 로(부스 `paperMm`, 없으면 사진 mm), `margin: 0` 으로 — 헤더/푸터는 여백 영역에
 *   그려지므로 함께 사라진다(Chrome). iPad Safari 는 다이얼로그 설정에 따라 남을 수 있어 실기 확인 항목.
 * - html/body·시트를 용지 mm 로 고정하고 넘침을 숨겨 반올림으로 빈 2페이지가 생기지 않게 한다.
 * - 사진은 `resolvePrintLayout` 이 준 **실제 mm 크기**로 용지 중앙에 절대 배치 — 용지가 사진보다 크면 그 차이가
 *   흰 여백(운영자가 "여백" 을 표현하는 방법), 같으면 오프셋 0 으로 기존 출력과 동일.
 * - 사진이 그대로는 안 들어가고 90° 돌리면 들어가면(`layout.rotate`) 사진 요소는 원래 치수로 두고 `rotate(90deg)`
 *   로 돌린다 — 예: 엽서 148×100 사진을 세로 100×148 용지에. iPad/드라이버가 가로 `@page` 를 무시할 때의 우회.
 * - 사진은 이미 프리셋 픽셀로 크롭·리사이즈돼 있어(useTransformJob fitTo) cover 는 실질 무손실 — fitTo 를 못 거친
 *   폴백 이미지의 비율 차이·반올림만 가장자리 소량 크롭으로 흡수한다.
 *
 * 용지를 프리셋이 아니라 부스에서 받는 이유: 프린터에 물린 종이는 부스마다 다른데 프리셋은 전역 문서다.
 * 그리고 드라이버가 페이지 박스를 실제 용지에 맞춰 축소하므로, 페이지 = 실물 용지일 때만 사진이 지정한 mm 로
 * 인쇄된다 — 용지 값은 여백 옵션이 아니라 크기 고정 수단이다. 값이 실물과 다르면 배율이 오히려 틀어진다.
 */
export function printSheetCss(preset: PrintPreset, paper?: SizeMm | null): string {
  const layout = resolvePrintLayout(preset, paper);
  const pw = mm(layout.page.widthMm);
  const ph = mm(layout.page.heightMm);
  // 회전이면 <img> 자체는 사진 원래 치수(가로×세로)로 그리고, 박스 중심에 맞춰 90° 돌린다.
  // translate(-50%,-50%) 는 요소 자신의 치수 기준이라 회전과 무관하게 중심을 잡아 준다.
  const img = layout.rotate
    ? `left: ${mm(layout.offset.xMm + layout.photo.widthMm / 2)}; top: ${mm(layout.offset.yMm + layout.photo.heightMm / 2)}; width: ${mm(preset.widthMm)}; height: ${mm(preset.heightMm)}; transform: translate(-50%, -50%) rotate(90deg);`
    : `left: ${mm(layout.offset.xMm)}; top: ${mm(layout.offset.yMm)}; width: ${mm(layout.photo.widthMm)}; height: ${mm(layout.photo.heightMm)};`;
  return `
@page { size: ${pw} ${ph}; margin: 0; }
@media print {
  html, body { margin: 0; padding: 0; width: ${pw}; height: ${ph}; overflow: hidden; }
  .app, video { display: none !important; }
  #print-sheet { display: block; position: fixed; inset: 0; width: ${pw}; height: ${ph}; overflow: hidden; }
  #print-sheet img { display: block; position: absolute; ${img} object-fit: cover; }
}
`;
}
